<template>
  <div class="d-flex flex-column">
    <v-autocomplete
      v-if="!advanced && vertical"
      v-model="filter"
      :placeholder="$t(placeholder)"
      :prepend-inner-icon="icon"
      hide-no-data
      hide-details
      return-object
      auto-select-first
      no-filter
      clearable
      :items="searchItems"
      :filled="filled"
      :key="filter"
      :loading="loadingFilter || !dataSet"
      :search-input.sync="filterValue"
      :menu-props="{ maxHeight: $vuetify.breakpoint.smAndDown ? 355 : 900 }"
      :disabled="!dataSet || disabled"
      :append-icon="advanced ? '' : '$dropdown'"
      direction="vertical"
      @change="onAutoCompleteChange($event)"
      @click="onInputClick()"
      @focus="onInputFocus()"
      ref="autocomplete"
    >
      <template slot="progress" >
        <div class="d-flex align-center">
          <v-progress-circular
            :size="32"
            class="mx-1"
            color="secondary"
            indeterminate
          ></v-progress-circular>
        </div>
      </template>
      <template slot="item" slot-scope="data">
        <div class="w-100">
          <PlayerSearchItem
            :player="data.item.value"
            v-if="data.item.type === 'player'"
          ></PlayerSearchItem>
          <TeamSearchItem
            :team="data.item.value"
            v-if="data.item.type === 'team'"
          ></TeamSearchItem>
          <GameSearchItem
            :game="data.item.value"
            v-if="data.item.type === 'game'"
          ></GameSearchItem>
          <LeagueSearchItem
            :league="data.item.value"
            v-if="data.item.type === 'league'"
          ></LeagueSearchItem>
          <div class v-if="data.item.type === 'allResults'">
            <span class="primary--text font-weight-bold">{{
              $t("searchbar.explore")
            }}</span>
          </div>
        </div>
      </template>
    </v-autocomplete>

    <v-menu
      bottom
      offset-y
      nudge-bottom="2"
      min-width="666"
      :key="`vertical-`+filter"
      ref="menuSearchbarRef"
      v-if="!advanced && !vertical"
      :open-on-click="false"
      :close-on-click="false"
      :close-on-content-click="false"
      :value="menuVisible"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-autocomplete
          v-on="on"
          v-bind="attrs"
          v-model="filter"
          :placeholder="$t(placeholder)"
          :prepend-inner-icon="icon"
          hide-no-data
          hide-details
          return-object
          auto-select-first
          no-filter
          clearable
          :items="searchItems"
          :filled="filled"
          :key="filter"
          :loading="loadingFilter || !dataSet"
          :search-input.sync="filterValue"
          :menu-props="{ disabled:true }"
          :disabled="!dataSet || disabled"
          :append-icon="advanced ? '' : '$dropdown'"
          direction="vertical"
          @change="onAutoCompleteChange($event)"
          @click="onInputClick()"
          @focus="onInputFocus()"
          @blur="onInputBlur()"
          ref="autocomplete"
        >
          <template slot="progress" >
            <div class="d-flex align-center">
              <v-progress-circular
                :size="32"
                class="mx-1"
                color="secondary"
                indeterminate
              ></v-progress-circular>
            </div>
          </template>
        </v-autocomplete>
      </template>

      <div class="d-flex flex-column" :class="$vuetify.theme.dark ? 'black' : 'white'" v-if="searchItems.length">
        <div class="d-flex flex-row">
          <div class="w-50">
            <div class="px-4">
              <v-divider></v-divider>
              <span class="body-2 primary--text font-weight-bold">{{$t("searchbar.players")}}</span>
              <PlayerSearchItem v-for="(item,i) of playerSearchItems" :key="i" :player="item.value" allowRouter></PlayerSearchItem>
              <v-divider></v-divider>
            </div>
          </div>
          <div class="w-50">
            <div class="px-4">
              <v-divider></v-divider>
              <span class="body-2 primary--text font-weight-bold">{{$t("searchbar.teams")}}</span>
              <TeamSearchItem v-for="(item,i) of teamSearchItems" :key="i" :team="item.value" allowRouter></TeamSearchItem>
              <v-divider></v-divider>
            </div>
            <div class="px-4">
              <v-divider></v-divider>
              <span class="body-2 primary--text font-weight-bold">{{$t("searchbar.games")}}</span>
              <GameSearchItem v-for="(item,i) of gameSearchItems" :key="i" :game="item.value" allowRouter></GameSearchItem>
              <v-divider></v-divider>
            </div>
            <div class="px-4">
              <v-divider></v-divider>
              <span class="body-2 primary--text font-weight-bold">{{$t("searchbar.leagues")}}</span>
              <LeagueSearchItem v-for="(item,i) of leagueSearchItems" :key="i" :league="item.value" allowRouter></LeagueSearchItem>
              <v-divider></v-divider>
            </div>
          </div>
        </div>
        <div class="pa-3 lighten-1" :class="$vuetify.theme.dark ? 'black' : 'white'">
          <v-btn class="elevation-0 px-4 monospace-font" rounded :class="$vuetify.theme.dark ? 'secondary' : 'white'" block @click="onAutoCompleteChange({type:'allResults'})">{{$t("searchbar.explore")}}</v-btn>
        </div>
      </div>
    </v-menu>

    <v-text-field
      v-if="advanced"
      :filled="filled"
      v-model="filter"
      placeholder="Placeholder"
      hide-details
      @keydown.enter="onExploreClick()"
    ></v-text-field>
    <v-btn
      v-if="advanced"
      color="primary"
      block
      tile
      x-small
      class="rounded-b"
      :disabled="!filter || filter.length < 3"
      @click="onExploreClick()"
      >{{ $t("general.search") }}</v-btn
    >
  </div>
</template>

<script>
import PlayerSearchItem from "../player/PlayerSearchItem";
import TeamSearchItem from "../team/TeamSearchItem";
import GameSearchItem from "../game/GameSearchItem";
import LeagueSearchItem from "../base/LeagueSearchItem";
import SearchService from '../../services/search.service';
import {
  normalizeCaseAndAccents,
  calculateWeightFilterItem,
  calculateWeightPlayer
} from "../../utils/functions";
import * as _ from "lodash";
import * as moment from "moment";

export default {
  name: "SearchBar",
  components: {
    PlayerSearchItem,
    TeamSearchItem,
    GameSearchItem,
    LeagueSearchItem
  },
  data() {
    return {
      filter: null,
      searchItems: [],
      loadingFilter: false,
      filterValue: null,
      delay: null,
      delayOffset: 250,
      menuVisible: false
    };
  },
  computed: {
    dataSetSize() {
      return (
        _.size(this.dataSet.players) +
        _.size(this.dataSet.teams) +
        _.size(this.dataSet.games) +
        _.size(this.dataSet.leagues)
      );
    },
    playerSearchItems(){
      return _.filter(this.searchItems,(s) => s.type === 'player');
    },
    teamSearchItems(){
      return _.filter(this.searchItems,(s) => s.type === 'team');
    },
    gameSearchItems(){
      return _.filter(this.searchItems,(s) => s.type === 'game');
    },
    leagueSearchItems(){
      return _.filter(this.searchItems,(s) => s.type === 'league');
    }
  },
  props: {
    dataSet: {
      type: Object,
      default: null,
    },
    placeholder: {
      type: String,
      default: "searchbar.placeholder",
    },
    icon: {
      type: String,
      default: "search",
    },
    showResults: {
      type: Boolean,
      default: true,
    },
    showPlayers: {
      type: Boolean,
      default: true,
    },
    playersResults: {
      type: Number,
      default: 3,
    },
    showTeams: {
      type: Boolean,
      default: true,
    },
    teamsResults: {
      type: Number,
      default: 2,
    },
    showGames: {
      type: Boolean,
      default: true,
    },
    gamesResults: {
      type: Number,
      default: 3,
    },
    showLeagues: {
      type: Boolean,
      default: true,
    },
    leaguesResults: {
      type: Number,
      default: 1,
    },
    showMore: {
      type: Boolean,
      default: true,
    },
    navigate: {
      type: Boolean,
      default: true,
    },
    queryText: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    filled: {
      type: Boolean,
      default: false,
    },
    advanced: {
      type: Boolean,
      default: false,
    },
    noResultPlaceHolder: {
      type: Boolean,
      default: false
    },
    noResultPlaceHolderTemplate: {
      type: String,
      default: "",
    },
    vertical: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    filterValue(val) {
      if (!_.isNull(val)) {
        this.changeFilter(val);
      }
    },
    queryText(newVal, oldVal) {
      this.filter = newVal;
      this.filterValue = newVal;
      this.changeFilter(newVal, true);
    },
    dataSet(newDataSet, oldDataSet) {
      this.searchItems = [];
      if (this.queryText) {
        this.filterValue = this.queryText;
      }
    },
  },
  created() {},
  mounted() {},
  methods: {
    changeFilter(val, force = false) {
      if (!this.advanced || force) {
        this.onFilterChange(val);
      }
    },
    prepareSearchItems(results) {
      this.searchItems = [];
      if (this.showPlayers && results.players.length > 0) {
        this.searchItems.push({ header: this.$t("searchbar.players") });
        let players = _.chain(results.players)
          .slice(0, this.playersResults)
          .map((p) => {
            return {
              text: p.name,
              value: p,
              type: "player",
              id: p.id,
              weight: p.weight
            };
          })
          .value();
        this.searchItems = _.concat(this.searchItems, players);
        this.searchItems.push({ divider: true });
      } else if (this.noResultPlaceHolder) {
        this.searchItems.push({ header: this.$t("searchbar.placeholderNoResults") + " " + this.noResultPlaceHolderTemplate });
        this.searchItems.push({ divider: true });
      } else if(this.showPlayers){
        this.searchItems.push({ header: this.$t("searchbar.players") });
        this.searchItems.push({ divider: true });
      }
      if (this.showTeams) {
        this.searchItems.push({ header: this.$t("searchbar.teams") });
        let teams = _.chain(results.teams)
          .slice(0, this.teamsResults)
          .map((t) => {
            return {
              text: t.name,
              value: t,
              type: "team",
              id: t.id
            };
          })
          .value();
        this.searchItems = _.concat(this.searchItems, teams);
        this.searchItems.push({ divider: true });
      }
      if (this.showGames) {
        this.searchItems.push({ header: this.$t("searchbar.games") });
        let games = _.chain(results.games)
          .slice(0, this.gamesResults)
          .map((g) => {
            return {
              text: g.id,
              value: g,
              type: "game",
              id: g.id,
            };
          })
          .value();
        this.searchItems = _.concat(this.searchItems, games);
        this.searchItems.push({ divider: true });
      }
      if(this.showLeagues){
        this.searchItems.push({ header: this.$t("searchbar.leagues") });
        let leagues = _.chain(results.leagues)
          .slice(0, this.leaguesResults)
          .map((l) => {
            return {
              text: l.id,
              value: l,
              type: "league",
              id: l.id,
            };
          })
          .value();
        this.searchItems = _.concat(this.searchItems, leagues);
        this.searchItems.push({ divider: true });
      }
      if (this.showMore) {
        let showMoreObj = {
          text: "",
          type: "allResults",
          value: "allResults",
        }
        this.searchItems = this.$vuetify.breakpoint.mobile ? [showMoreObj,...this.searchItems] : [...this.searchItems,showMoreObj] ;
      }
    },
    onAutoCompleteChange(event) {
      if (event.type === "allResults") {
        if (this.navigate) {
          this.$router.push({
            name: "search",
            query: { qt: this.filterValue },
          });
        }
        this.filter = undefined;
        this.filterValue = undefined;
      } else {
        if (this.navigate) {
          if(event.type != 'league') this.$router.push({ name: event.type, params: { id: event.id } });
          else this.$router.push(`/league/${event.value.id}`);
        }
        this.filter = undefined;
        this.filterValue = undefined;
      }
      this.$emit("resultClicked", event);
    },
    onFilterChange(queryText) {
      clearTimeout(this.delay);
      if (_.size(queryText) >= 3 && this.dataSet) {
        this.loadingFilter = true;
        this.delay = setTimeout(
          async () => {
            let qNormalized = normalizeCaseAndAccents(queryText);
            let filteredPlayers;
            let filteredTeam;
            let filteredGames;
            let filteredLeagues;

            if(!_.size(this.dataSet.players) && this.showPlayers){
              let response = await SearchService.getPlayersDataSet(qNormalized,this.playersResults)
              filteredPlayers = response.data;
              let dataSetFiltered = {
                players: filteredPlayers,
                teams: filteredTeam,
                games: filteredGames,
                leagues: filteredLeagues
              };
              this.loadingFilter = false;
              this.$emit("resultsChange", dataSetFiltered);
              if (this.showResults) {
                this.prepareSearchItems(dataSetFiltered);
              }
            }

            if (this.advanced) {
              filteredPlayers = _.chain(this.dataSet.players)
                .forEach((p) => {
                  let weight = calculateWeightPlayer(
                    p,
                    qNormalized
                  );
                  p.weight = weight;
                  p.mv = p.mv || 0;
                })
                .orderBy(["weight", "mv", "name"], ["desc", "desc", "asc"])
                .value();
              filteredTeam = _.chain(this.dataSet.teams)
                .forEach((t) => {
                  let weight = calculateWeightFilterItem(
                    t,
                    qNormalized,
                    "team"
                  );
                  t.weight = weight;
                  t.mv = t.mv || 0;
                })
                .orderBy(["weight", "mv", "name"], ["asc", "desc", "asc"])
                .value();
              filteredGames = _.chain(this.dataSet.games)
                .forEach((g) => {
                  let weight = calculateWeightFilterItem(
                    g,
                    qNormalized,
                    "game"
                  );
                  g.weight = weight;
                  g.simpleDate = moment(g.date, "DD/MM/YYYY").format(
                    "YYYYMMDD"
                  );
                })
                .orderBy(
                  ["weight", "simpleDate", "id"],
                  ["asc", "desc", "desc"]
                )
                .value();
              filteredLeagues = _.chain(this.dataSet.leagues)
                .forEach((l) => {
                  let weight = calculateWeightFilterItem(
                    l,
                    qNormalized,
                    "league"
                  );
                  l.weight = weight;
                })
                .orderBy(["weight","rank","name", "id"], ["asc","asc","asc", "asc"])
                .value();
            } else {
              if(_.size(this.dataSet.players)){
                filteredPlayers = _.chain(this.dataSet.players)
                .forEach((p) => {
                  let weight = calculateWeightPlayer(
                    p,
                    qNormalized
                  );
                  p.weight = weight;
                  p.mv = p.mv || 0;
                })
                .orderBy(["weight", "mv", "name"], ["desc", "desc", "asc"])
                .value();
              }
              filteredTeam = _.chain(this.dataSet.teams)
                .forEach((t) => {
                  let weight = calculateWeightFilterItem(
                    t,
                    qNormalized,
                    "team"
                  );
                  t.weight = weight;
                  t.mv = t.mv || 0;
                })
                .orderBy(["weight", "mv", "name"], ["asc", "desc", "asc"])
                .value();

              filteredGames = _.chain(this.dataSet.games)
                .filter((g) => {
                  let querySplitted = _.split(qNormalized,' ');
                  let teamNamesIncludeQuery = _.every(querySplitted,(q) => normalizeCaseAndAccents(g.htName).includes(q) || normalizeCaseAndAccents(g.atName).includes(q))
                  return _.includes(g.searchValue, qNormalized) || teamNamesIncludeQuery;
                })
                .forEach((g) => {
                  g.simpleDate = moment(g.date, "DD/MM/YYYY").format(
                    "YYYYMMDD"
                  );
                })
                .orderBy(
                  ["simpleDate", "id"],
                  ["desc", "desc"]
                )
                .value();

              filteredLeagues = _.chain(this.dataSet.leagues)
                .forEach((l) => {
                  let weight = calculateWeightFilterItem(
                    l,
                    qNormalized,
                    "league"
                  );
                  l.weight = weight;
                })
                .orderBy(["weight","rank","name", "id"], ["asc","asc","asc", "asc"])
                .value();
            }
            let dataSetFiltered = {
              players: filteredPlayers,
              teams: filteredTeam,
              games: filteredGames,
              leagues: filteredLeagues
            };
            this.loadingFilter = false;
            this.$emit("resultsChange", dataSetFiltered);
            if (this.showResults) {
              this.prepareSearchItems(dataSetFiltered);
            }
          },
          this.advanced ? 0 : this.delayOffset
        );
      } else {
        this.$emit("resultsChange", []);
        this.searchItems = [];
      }
      this.menuVisible = true;
    },
    onExploreClick() {
      this.$emit("exploreClicked", this.filter);
      this.changeFilter(this.filter, true);
      this.filter = null;
    },
    onInputClick(){
      this.$emit("inputClicked")
    },
    onInputFocus(){
      this.$emit("inputFocused")
    },
    onInputBlur(){
      setTimeout(() => {
        this.menuVisible = false;
      },160);
    }
  },
  beforeDestroy(){
  },
  destroyed(){
  }
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/views/base/SearchBar.vue